import React, { useState } from 'react';
import { FaPlus, FaCalendarDays, FaCircleCheck, FaStar, FaRegStar, FaLink, FaEye, FaDownload, FaTrash, FaChevronLeft, FaChevronRight, FaXmark, FaCamera, FaCircleQuestion } from 'react-icons/fa6';

interface BasicInfo {
  phone: string;
  email: string;
  website?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip?: string;
  travelDistance?: string;
  tmRate?: string;
  tmRateLastUpdated?: string;
  uin?: string;
  ein?: string;
}

interface Ticket {  id: string;
  title: string;
  date: string; // ISO or friendly
}

interface PurchaseOrder {
  id: string;
  number: string;
  date?: string;
  status?: string;
}

interface Attachment {
  id?: string | number;
  fileName: string;
  size?: string;
  blobUrl?: string; // runtime only
  expirationDate?: string | null; // ISO date (yyyy-mm-dd)
  storageKey?: string | null;
  category?: string | null; // 'insurance_coi', 'insurance_wc', or general 'attachments'
  downloadUrl?: string; // pre-signed download URL from backend
}

interface CwDocItem {
  id?: string | number;
  fileName: string;
  size?: number;
}

// Simple inline calendar popover
interface CalendarProps {
  anchorFile: string;
  value?: string;
  onSelect: (iso: string) => void;
  onClear: () => void;
  onClose: () => void;
}

const CalendarPopover: React.FC<CalendarProps> = ({ value, onSelect, onClear, onClose }) => {
  const initial = value ? new Date(value + 'T00:00:00') : new Date();
  const [year, setYear] = useState(initial.getFullYear());
  const [month, setMonth] = useState(initial.getMonth()); // 0-based

  const monthStart = new Date(year, month, 1);
  const firstDay = monthStart.getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const weeks: (number | null)[][] = [];
  let current = 1 - firstDay;
  while (current <= daysInMonth) {
    const week: (number | null)[] = [];
    for (let d = 0; d < 7; d++) {
      if (current < 1 || current > daysInMonth) week.push(null); else week.push(current);
      current++;
    }
    weeks.push(week);
  }
  const monthLabel = new Date(year, month, 1).toLocaleString(undefined, { month: 'long', year: 'numeric' });

  const toIso = (day: number) => {
    const m = (month + 1).toString().padStart(2, '0');
    const d = day.toString().padStart(2, '0');
    return `${year}-${m}-${d}`;
  };

  const selected = value || '';
  return (
    <div className="cal-popover" role="dialog" aria-label="Choose expiration date">
      <div className="cal-header">
        <button onClick={() => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); }} aria-label="Previous month"><FaChevronLeft /></button>
        <div className="cal-title">{monthLabel}</div>
        <button onClick={() => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); }} aria-label="Next month"><FaChevronRight /></button>
      </div>
      <div className="cal-grid cal-dow">
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <div key={d} className="cal-cell cal-dow-cell">{d}</div>)}
      </div>
      <div className="cal-grid">
        {weeks.map((w,i) => w.map((day,j) => {
          if (day === null) return <div key={i+'-'+j} className="cal-cell empty" />;
          const iso = toIso(day);
            const isSel = iso === selected;
          return (
            <button
              key={i+'-'+j}
              className={`cal-cell day${isSel ? ' selected' : ''}`}
              onClick={() => { onSelect(iso); onClose(); }}
            >{day}</button>
          );
        }))}
      </div>
      <div className="cal-footer">
        <button className="mini" onClick={() => { onClear(); onClose(); }} disabled={!selected}>Clear</button>
        <button className="mini" onClick={onClose}>Close</button>
      </div>
    </div>
  );
};

type NameOrItem = string | { id?: number; name: string };

interface Props {
  name: string;
  verified?: boolean;
  rating?: number; // 0-5
  status?: 'Active' | 'Inactive' | 'Pending';
  basic: BasicInfo;
  certifications?: NameOrItem[];
  services?: NameOrItem[];
  allCertifications?: NameOrItem[]; // Global vocabulary
  allServices?: NameOrItem[]; // Global vocabulary
  tickets?: Ticket[];
  cwTicketsHasMore?: boolean;
  onCwTicketsPageChange?: (page: number) => void;
  purchaseOrders?: PurchaseOrder[];
  cwPurchaseOrdersHasMore?: boolean;
  onCwPurchaseOrdersPageChange?: (page: number) => void;
  cwCompanyIdentifier?: string; // CW company identifier for PO links
  attachments?: Attachment[];
  cwNotes?: string | null;
  cwNotesItems?: { id?: string | number; date?: string; by?: string; text: string }[];
  cwNotesHasMore?: boolean;
  onCwNotesPageChange?: (page: number) => void;
  avatar?: string; // external avatar (data URL)
  onAvatarChange?: (dataUrl: string | null) => void;
  onAddAttachments?: (files: File[]) => void;
  onRemoveAttachment?: (attachmentId: string | number) => void;
  onSetAttachmentExpiry?: (attachmentId: string | number, isoDate?: string) => void;
  onVerifiedChange?: (value: boolean) => void;
  onNameChange?: (name: string) => void;
  onBasicChange?: (field: keyof BasicInfo, value: string) => void;
  onRatingChange?: (rating: number) => void;
  onStatusChange?: (status: 'Active' | 'Inactive' | 'Pending') => void;
  onAddCertification?: (name: string) => void;
  onRemoveCertification?: (name: string) => void;
  onAddService?: (name: string) => void;
  onRemoveService?: (name: string) => void;
  // Primary contact (from ConnectWise company primary/ default contact)
  primaryContact?: { id?: number; name?: string; title?: string; email?: string | null; phone?: string | null } | null;
  // Notes with auto-save
  notes?: string;
  notesSaving?: boolean;
  onNotesChange?: (notes: string) => void;
  // CW loading state
  cwLoading?: boolean;
  // Pin color
  pinColor?: string;
  onPinColorChange?: (color: string) => void;
}

const starArray = (count: number) => Array.from({ length: 5 }, (_, i) => i < count);

export const SubcontractorPanel: React.FC<Props> = ({
  name,
  verified,
  rating = 0,
  status = 'Pending',
  basic,
  certifications = [],
  services = [],
  allCertifications = [],
  allServices = [],
  tickets = [],
  cwTicketsHasMore,
  onCwTicketsPageChange,
  purchaseOrders = [],
  cwPurchaseOrdersHasMore,
  onCwPurchaseOrdersPageChange,
  cwCompanyIdentifier,
  attachments = [],
  cwNotes = null,
  cwNotesItems = [],
  cwNotesHasMore,
  onCwNotesPageChange,
  avatar,
  onAvatarChange,
  onAddAttachments,
  onRemoveAttachment,
  onSetAttachmentExpiry,
  onVerifiedChange,
  onNameChange,
  onBasicChange,
  onRatingChange,
  onStatusChange,
  onAddCertification,
  onRemoveCertification,
  onAddService,
  onRemoveService,
  primaryContact,
  notes,
  notesSaving,
  onNotesChange,
  cwLoading,
  pinColor = '#ff7e00',
  onPinColorChange
}) => {
  const [open, setOpen] = useState(true);
  const toName = (v: NameOrItem) => typeof v === 'string' ? v : (v?.name ?? '').toString();
  const [certs, setCerts] = useState<string[]>(certifications.map(toName));
  const [servs, setServs] = useState<string[]>(services.map(toName));
  const [basicInfo, setBasicInfo] = useState<BasicInfo>(basic);
  const [editingField, setEditingField] = useState<keyof BasicInfo | null>(null);
  const [tempValue, setTempValue] = useState('');
  const [avatarSrc, setAvatarSrc] = useState<string | null>(avatar || null);
  const [ratingValue, setRatingValue] = useState<number>(rating);
  const [nameValue, setNameValue] = useState<string>(name);
  const [editingName, setEditingName] = useState<boolean>(false);
  const [certDropdownOpen, setCertDropdownOpen] = useState<boolean>(false);
  const [svcDropdownOpen, setSvcDropdownOpen] = useState<boolean>(false);
  const [newCertInput, setNewCertInput] = useState<string>('');
  const [newSvcInput, setNewSvcInput] = useState<string>('');
  const [showCertCustomInput, setShowCertCustomInput] = useState<boolean>(false);
  const [showSvcCustomInput, setShowSvcCustomInput] = useState<boolean>(false);
  const [attachmentItems, setAttachmentItems] = useState<Attachment[]>(attachments);
  const certDropdownRef = React.useRef<HTMLDivElement | null>(null);
  const svcDropdownRef = React.useRef<HTMLDivElement | null>(null);
  const [editingExpiryFor, setEditingExpiryFor] = useState<string | null>(null);
  const attachmentInputRef = React.useRef<HTMLInputElement | null>(null);
  
  // COI and WC documents
  const [coiDocument, setCoiDocument] = useState<Attachment | null>(null);
  const [wcDocument, setWcDocument] = useState<Attachment | null>(null);
  const coiInputRef = React.useRef<HTMLInputElement | null>(null);
  const wcInputRef = React.useRef<HTMLInputElement | null>(null);
  // Documents removed; only tickets and notes remain paginated
  const [cwNotesPage, setCwNotesPage] = useState(1);
  const notesPageSize = 5;

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  // Keep avatar and attachments in sync with props from parent
  React.useEffect(() => { setAvatarSrc(avatar || null); }, [avatar]);
  
  // Separate COI/WC from general attachments and load into respective states
  React.useEffect(() => {
    const allAttachments = attachments || [];
    console.log('[SubcontractorPanel] Filtering attachments:', allAttachments.map(a => ({ name: a.fileName, category: a.category })));
    const coi = allAttachments.find(a => a.category === 'insurance_coi');
    const wc = allAttachments.find(a => a.category === 'insurance_wc');
    const general = allAttachments.filter(a => !a.category || (a.category !== 'insurance_coi' && a.category !== 'insurance_wc'));
    
    console.log('[SubcontractorPanel] Separated:', { coi: coi?.fileName, wc: wc?.fileName, general: general.map(g => g.fileName) });
    setCoiDocument(coi || null);
    setWcDocument(wc || null);
    setAttachmentItems(general);
  }, [JSON.stringify(attachments)]);
  
  React.useEffect(() => { setCwNotesPage(1); }, [JSON.stringify(cwNotesItems)]);

  const [ticketPage, setTicketPage] = useState(1);
  const ticketsPageSize = 5;
  const [poPage, setPoPage] = useState(1);
  const poPageSize = 5;
  const [showRatingTooltip, setShowRatingTooltip] = useState(false);
  // Keep avatar in sync with prop changes from parent (e.g., after upload->server URL->data URL conversion)
  React.useEffect(() => {
    setAvatarSrc(avatar || null);
  }, [avatar]);

  React.useEffect(() => { setTicketPage(1); }, [JSON.stringify(tickets)]);
  React.useEffect(() => { setPoPage(1); }, [JSON.stringify(purchaseOrders)]);
  const toggleCert = (val: string) => {
    if (certs.includes(val)) {
      setCerts(prev => prev.filter(c => c !== val));
      onRemoveCertification?.(val);
    } else {
      setCerts(prev => [...prev, val]);
      onAddCertification?.(val);
    }
  };
  
  const toggleService = (val: string) => {
    if (servs.includes(val)) {
      setServs(prev => prev.filter(s => s !== val));
      onRemoveService?.(val);
    } else {
      setServs(prev => [...prev, val]);
      onAddService?.(val);
    }
  };
  
  const addCustomCert = () => {
    const val = newCertInput.trim();
    if (val && !certs.includes(val)) {
      setCerts(prev => [...prev, val]);
      onAddCertification?.(val);
    }
    setNewCertInput('');
    setShowCertCustomInput(false);
  };
  
  const addCustomService = () => {
    const val = newSvcInput.trim();
    if (val && !servs.includes(val)) {
      setServs(prev => [...prev, val]);
      onAddService?.(val);
    }
    setNewSvcInput('');
    setShowSvcCustomInput(false);
  };
  
  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (certDropdownRef.current && !certDropdownRef.current.contains(e.target as Node)) {
        setCertDropdownOpen(false);
        setShowCertCustomInput(false);
        setNewCertInput('');
      }
      if (svcDropdownRef.current && !svcDropdownRef.current.contains(e.target as Node)) {
        setSvcDropdownOpen(false);
        setShowSvcCustomInput(false);
        setNewSvcInput('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const commitName = () => {
    setEditingName(false);
    if (!nameValue.trim()) { setNameValue(name); return; }
    onNameChange?.(nameValue.trim());
  };
  // Sync down from parent if props change externally
  React.useEffect(() => { setCerts(certifications.map(toName)); }, [JSON.stringify(certifications)]);
  React.useEffect(() => { setServs(services.map(toName)); }, [JSON.stringify(services)]);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const onAvatarFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const result = ev.target?.result;
      if (typeof result === 'string') {
        setAvatarSrc(result);
        onAvatarChange?.(result);
      }
    };
    reader.readAsDataURL(file);
  };

  const startEdit = (field: keyof BasicInfo) => {
    setEditingField(field);
    setTempValue(basicInfo[field] ?? '');
  };

  const commitEdit = () => {
    if (editingField === null) return;
    const value = tempValue.trim();
    setBasicInfo(prev => ({ ...prev, [editingField]: value }));
  onBasicChange?.(editingField, value);
    setEditingField(null);
    setTempValue('');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setTempValue('');
  };

  const formatSize = (bytes: number) => {
    if (!Number.isFinite(bytes)) return '';
    const units = ['B','KB','MB','GB'];
    let u = 0; let val = bytes;
    while (val >= 1024 && u < units.length - 1) { val /= 1024; u++; }
    return `${val.toFixed(val < 10 && u>0 ? 1 : 0)} ${units[u]}`;
  };

  const formatDateMMDDYYYY = (input?: string | null) => {
    if (!input) return '';
    // If already YYYY-MM-DD, transform directly
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
      const [y, m, d] = input.split('-');
      return `${m}-${d}-${y}`;
    }
    // Else try to parse full ISO and format
    const dt = new Date(input);
    if (isNaN(dt.getTime())) return String(input);
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    const yyyy = dt.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  };

  const formatTimestamp = (isoString?: string) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return null;
    }
  };

  const handleAddAttachmentClick = () => {
    attachmentInputRef.current?.click();
  };

  const onAttachmentFilesChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    // Don't update local state immediately - let backend refresh handle it after upload
    // bubble up for upload/persist
    try { onAddAttachments?.(Array.from(files)); } catch {}
    e.target.value = '';
  };

  const removeAttachment = (fileNameOrId: string | number) => {
    // Find by ID if numeric/guid-like, otherwise by fileName
    const att = attachmentItems.find(a => 
      String(a.id) === String(fileNameOrId) || a.fileName === fileNameOrId
    );
    if (!att) return;
    
    // Update local state
    setAttachmentItems(prev => prev.filter(a => a !== att));
    
    // Notify parent to delete from backend
    if (att.id !== undefined && att.id !== null) {
      try { onRemoveAttachment?.(att.id); } catch { /* ignore */ }
    }
  };

  const openAttachment = (att: Attachment) => {
    // Priority: blobUrl (newly uploaded) > downloadUrl (pre-signed from backend) > fallback
    const url = att.blobUrl || 
                att.downloadUrl || 
                (att as any).download_url || 
                (att.storageKey ? `/files?key=${encodeURIComponent(att.storageKey)}&disposition=inline` : null);
    if (url) {
      window.open(url, '_blank');
    } else {
      console.warn('Cannot open attachment: no blobUrl, downloadUrl, or storageKey', att);
    }
  };

  const downloadAttachment = (att: Attachment) => {
    // Priority: blobUrl (newly uploaded) > downloadUrl (pre-signed from backend) > fallback
    const url = att.blobUrl || 
                att.downloadUrl || 
                (att as any).download_url || 
                (att.storageKey ? `/files?key=${encodeURIComponent(att.storageKey)}&disposition=attachment` : null);
    if (!url) {
      console.warn('Cannot download attachment: no blobUrl, downloadUrl, or storageKey', att);
      return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = att.fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const setAttachmentExpiration = (fileName: string, isoDate?: string) => {
    // Check if this is a COI or WC document (prefixed)
    if (fileName.startsWith('coi_')) {
      const actualFileName = fileName.substring(4);
      setCoiDocument(prev => prev ? { ...prev, expirationDate: isoDate ?? null } : null);
      if (coiDocument?.id !== undefined && coiDocument?.id !== null) {
        try { onSetAttachmentExpiry?.(coiDocument.id, isoDate); } catch { /* ignore */ }
      }
      return;
    }
    if (fileName.startsWith('wc_')) {
      const actualFileName = fileName.substring(3);
      setWcDocument(prev => prev ? { ...prev, expirationDate: isoDate ?? null } : null);
      if (wcDocument?.id !== undefined && wcDocument?.id !== null) {
        try { onSetAttachmentExpiry?.(wcDocument.id, isoDate); } catch { /* ignore */ }
      }
      return;
    }
    // Regular attachment
    setAttachmentItems(prev => prev.map(a => a.fileName === fileName ? { ...a, expirationDate: isoDate ?? null } : a));
    // Notify parent to persist if we know the server id
    const att = attachmentItems.find(a => a.fileName === fileName);
    if (att && att.id !== undefined && att.id !== null) {
      try { onSetAttachmentExpiry?.(att.id, isoDate); } catch { /* ignore */ }
    }
  };

  // COI handlers
  const handleAddCoiClick = () => {
    coiInputRef.current?.click();
  };

  const onCoiFileChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    // Create file with metadata marker for category
    const markedFile = Object.assign(file, { __category: 'insurance_coi' });
    console.log('[COI Upload] Marked file with category:', (markedFile as any).__category, 'file:', file.name);
    // Don't update local state immediately - let backend refresh handle it after upload
    try { onAddAttachments?.([markedFile]); } catch {}
    e.target.value = '';
  };

  const removeCoiDocument = () => {
    if (coiDocument?.id !== undefined && coiDocument?.id !== null) {
      removeAttachment(coiDocument.id);
    }
    setCoiDocument(null);
  };

  // WC handlers
  const handleAddWcClick = () => {
    wcInputRef.current?.click();
  };

  const onWcFileChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    // Create file with metadata marker for category
    const markedFile = Object.assign(file, { __category: 'insurance_wc' });
    // Don't update local state immediately - let backend refresh handle it after upload
    try { onAddAttachments?.([markedFile]); } catch {}
    e.target.value = '';
  };

  const removeWcDocument = () => {
    if (wcDocument?.id !== undefined && wcDocument?.id !== null) {
      removeAttachment(wcDocument.id);
    }
    setWcDocument(null);
  };

  // Expiry badge displays ISO date directly for now

  const renderBasicField = (field: keyof BasicInfo, label: string, options?: { link?: boolean }) => {
    const val = basicInfo[field];
    const isEditing = editingField === field;
    return (
      <div className="kv" key={field}>
        <label>{label}</label>
        {isEditing ? (
          <span style={{ flex:1 }}>
            <input
              className="edit-input"
              autoFocus
              value={tempValue}
              onChange={e => setTempValue(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={e => {
                if (e.key === 'Enter') commitEdit();
                if (e.key === 'Escape') cancelEdit();
              }}
            />
          </span>
        ) : (
          <span
            className="editable"
            onClick={() => startEdit(field)}
            title="Click to edit"
          >
            {options?.link && val ? (
              <a href={val} onClick={e => { e.stopPropagation(); }}>
                {val} <FaLink style={{ marginLeft: 4, fontSize: '0.75em' }} />
              </a>
            ) : (val || '—')}
            {field === 'tmRate' && basicInfo.tmRateLastUpdated && (
              <span style={{ fontSize: '10px', color: '#888', marginLeft: '8px', display: 'block' }}>
                Last updated: {formatTimestamp(basicInfo.tmRateLastUpdated)}
              </span>
            )}
          </span>
        )}
      </div>
    );
  };

  const basicFieldConfig: { field: keyof BasicInfo; label: string; link?: boolean; condition?: (b: BasicInfo) => boolean }[] = [
    { field: 'phone', label: 'Phone' },
    { field: 'email', label: 'Email' },
    { field: 'website', label: 'Website', link: true },
    { field: 'address1', label: 'Address Line 1' },
    { field: 'address2', label: 'Address Line 2' },
    { field: 'city', label: 'City' },
    { field: 'state', label: 'State' },
    { field: 'zip', label: 'Zip' },
    { field: 'travelDistance', label: 'Travel Distance' },
    { field: 'tmRate', label: 'T&M Rate' },
    { field: 'uin', label: 'UIN' },
    { field: 'ein', label: 'EIN' }
  ];
  return (
    <aside className={`sc-panel ${open ? 'open' : 'closed'}`}>
      <button className="toggle" onClick={() => setOpen(o => !o)} title={open ? 'Collapse' : 'Expand'}>
        {open ? <FaChevronRight /> : <FaChevronLeft />}
      </button>
      <div className="panel-inner">
        <div className="profile">
          <div
            className={`avatar ${avatarSrc ? 'has-image' : 'empty'}`}
            onClick={handleAvatarClick}
            role="button"
            tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleAvatarClick(); } }}
            title="Click to upload profile image"
          >
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={name}
                onError={() => {
                  // Fallback to placeholder if the image fails to load
                  setAvatarSrc(null);
                }}
              />
            ) : (
              <FaCamera className="avatar-placeholder-icon" />
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={onAvatarFileChange}
            />
          </div>
          <div className="identity">
            <div className="name-row">
              {editingName ? (
                <input
                  className="edit-name"
                  value={nameValue}
                  autoFocus
                  onChange={e => setNameValue(e.target.value)}
                  onBlur={commitName}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitName();
                    if (e.key === 'Escape') { setNameValue(name); setEditingName(false); }
                  }}
                />
              ) : (
                <h2 className="editable" title="Click to edit name" onClick={() => setEditingName(true)}>{nameValue}</h2>
              )}
              <button
                className={`verified-btn ${verified ? 'on' : 'off'}`}
                title={verified ? 'Click to unverify' : 'Click to verify'}
                aria-pressed={!!verified}
                onClick={() => onVerifiedChange?.(!verified)}
              >
                <FaCircleCheck />
              </button>
            </div>
            <div className="rating" role="radiogroup" aria-label="Rating">
              {starArray(5).map((_, i) => {
                const filled = i < ratingValue;
                return (
                  <span
                    key={i}
                    role="radio"
                    aria-checked={filled}
                    tabIndex={0}
                    className={filled ? 'star full' : 'star'}
                    onClick={() => { setRatingValue(i + 1); onRatingChange?.(i + 1); }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setRatingValue(i + 1); onRatingChange?.(i + 1); }
                      if (e.key === 'ArrowLeft') { setRatingValue(v => { const nv = Math.max(1, v - 1); onRatingChange?.(nv); return nv; }); }
                      if (e.key === 'ArrowRight') { setRatingValue(v => { const nv = Math.min(5, v + 1); onRatingChange?.(nv); return nv; }); }
                    }}
                    title={`Set rating to ${i + 1}`}
                  >
                    {filled ? <FaStar /> : <FaRegStar />}
                  </span>
                );
              })}
              <span 
                style={{ 
                  marginLeft: '8px', 
                  color: '#888', 
                  cursor: 'help',
                  fontSize: '14px',
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
                onMouseEnter={() => setShowRatingTooltip(true)}
                onMouseLeave={() => setShowRatingTooltip(false)}
              >
                <FaCircleQuestion />
                {showRatingTooltip && (
                  <div 
                    style={{
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      top: '24px',
                      background: '#333',
                      color: 'white',
                      padding: '10px 14px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      whiteSpace: 'normal',
                      width: '400px',
                      zIndex: 10000,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      lineHeight: '1.5'
                    }}
                    onMouseEnter={() => setShowRatingTooltip(true)}
                    onMouseLeave={() => setShowRatingTooltip(false)}
                  >
                    <div style={{ marginBottom: '6px' }}><strong>5 star:</strong> Can send on their own, multiple completed jobs, <br></br> experienced, partner-like</div>
                    <div style={{ marginBottom: '6px' }}><strong>4 star:</strong> Did a great job, communicative, <br></br> used multiple times, reliable</div>
                    <div style={{ marginBottom: '6px' }}><strong>3 star:</strong> Shows up, does what is asked, <br></br> eventually sends the invoice, nothing special</div>
                    <div style={{ marginBottom: '6px' }}><strong>2 star:</strong> If we have to use them, keep on a tight leash, spell <br></br> out the scope, require pics, and use best judgement on <br></br> the client they are attending</div>
                    <div><strong>1 star:</strong> DO NOT USE, don't delete from list, so another <br></br> person doesn't re-add them, like a blackball list</div>
                  </div>
                )}
              </span>
            </div>
          </div>
        </div>
        <section>
          <h3>Basic Info</h3>
          {basicFieldConfig.map(cfg => (
            // Always render to allow adding even if empty; could hide if undefined by adding condition
            renderBasicField(cfg.field, cfg.label, { link: cfg.link })
          ))}
        </section>
        <section>
          <h3>Status</h3>
          <select 
            value={status} 
            onChange={e => onStatusChange?.(e.target.value as 'Active' | 'Inactive' | 'Pending')}
            style={{ width: '100%', padding: '8px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Pending">Pending</option>
          </select>
        </section>
        <section>
          <h3>Pin Color</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="color"
              value={pinColor}
              onChange={e => onPinColorChange?.(e.target.value)}
              style={{
                width: '60px',
                height: '36px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              title="Select pin color"
            />
            <span style={{ fontSize: '14px', color: '#888' }}>
              Color for map pin and service area
            </span>
          </div>
        </section>
        <section style={{ position: 'relative' }}>
          <h3>Primary Contact (ConnectWise)</h3>
          {cwLoading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              zIndex: 10
            }}>
              <span style={{ color: '#fff', fontSize: '12px' }}>Loading...</span>
            </div>
          )}
          {primaryContact ? (
            <div>
              <div className="kv"><label>Name</label><span>{primaryContact.name || '—'}</span></div>
              <div className="kv"><label>Title</label><span>{primaryContact.title || '—'}</span></div>
              <div className="kv"><label>Email</label><span>{primaryContact.email ? <a href={`mailto:${primaryContact.email}`}>{primaryContact.email}</a> : '—'}</span></div>
              <div className="kv"><label>Phone</label><span>{primaryContact.phone ? <a href={`tel:${primaryContact.phone}`}>{primaryContact.phone}</a> : '—'}</span></div>
            </div>
          ) : (
            <div className="empty-hint">Not available</div>
          )}
        </section>
  {/* ConnectWise Documents section removed intentionally */}
        <section>
          <h3>Certifications</h3>
          <div className="tags" style={{ marginBottom: '8px' }}>
            {certs.map(c => (
              <span key={c} className="tag cert removable">{c}
                <button className="tag-remove" onClick={() => toggleCert(c)} aria-label={`Remove ${c}`} title="Remove">
                  <FaXmark />
                </button>
              </span>
            ))}
            {certs.length === 0 && (
              <span className="empty-hint">None selected</span>
            )}
          </div>
          <div className="dropdown-wrapper" ref={certDropdownRef}>
            <button 
              className="dropdown-trigger"
              onClick={() => setCertDropdownOpen(!certDropdownOpen)}
            >
              {certs.length > 0 ? `${certs.length} selected` : 'Select certifications...'}
            </button>
            {certDropdownOpen && (
              <div className="dropdown-menu">
                {allCertifications.map(cert => {
                  const certName = toName(cert);
                  return (
                    <label key={certName} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={certs.includes(certName)}
                        onChange={() => toggleCert(certName)}
                      />
                      <span>{certName}</span>
                    </label>
                  );
                })}
                {showCertCustomInput ? (
                  <div className="dropdown-custom-input">
                    <input
                      type="text"
                      placeholder="Enter custom certification"
                      value={newCertInput}
                      autoFocus
                      onChange={e => setNewCertInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') addCustomCert();
                        if (e.key === 'Escape') { setShowCertCustomInput(false); setNewCertInput(''); }
                      }}
                    />
                    <button onClick={addCustomCert}>Add</button>
                  </div>
                ) : (
                  <button 
                    className="dropdown-add-custom"
                    onClick={() => setShowCertCustomInput(true)}
                  >
                    + Add custom...
                  </button>
                )}
              </div>
            )}
          </div>
        </section>
        <section>
          <h3>Services</h3>
          <div className="tags" style={{ marginBottom: '8px' }}>
            {servs.map(s => (
              <span key={s} className="tag svc removable">{s}
                <button className="tag-remove" onClick={() => toggleService(s)} aria-label={`Remove ${s}`} title="Remove">
                  <FaXmark />
                </button>
              </span>
            ))}
            {servs.length === 0 && (
              <span className="empty-hint">None selected</span>
            )}
          </div>
          <div className="dropdown-wrapper" ref={svcDropdownRef}>
            <button 
              className="dropdown-trigger"
              onClick={() => setSvcDropdownOpen(!svcDropdownOpen)}
            >
              {servs.length > 0 ? `${servs.length} selected` : 'Select services...'}
            </button>
            {svcDropdownOpen && (
              <div className="dropdown-menu">
                {allServices.map(svc => {
                  const svcName = toName(svc);
                  return (
                    <label key={svcName} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={servs.includes(svcName)}
                        onChange={() => toggleService(svcName)}
                      />
                      <span>{svcName}</span>
                    </label>
                  );
                })}
                {showSvcCustomInput ? (
                  <div className="dropdown-custom-input">
                    <input
                      type="text"
                      placeholder="Enter custom service"
                      value={newSvcInput}
                      autoFocus
                      onChange={e => setNewSvcInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') addCustomService();
                        if (e.key === 'Escape') { setShowSvcCustomInput(false); setNewSvcInput(''); }
                      }}
                    />
                    <button onClick={addCustomService}>Add</button>
                  </div>
                ) : (
                  <button 
                    className="dropdown-add-custom"
                    onClick={() => setShowSvcCustomInput(true)}
                  >
                    + Add custom...
                  </button>
                )}
              </div>
            )}
          </div>
        </section>
        <section style={{ position: 'relative' }}>
          <h3 className="row-space">
            <span>Purchase Orders</span>
            <span style={{ fontSize: 12, color: '#aaa' }}>
              {purchaseOrders.length > 0
                ? `${(poPage - 1) * poPageSize + 1}-${(poPage - 1) * poPageSize + purchaseOrders.length}`
                : '0 of 0'}
            </span>
          </h3>
          {cwLoading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              zIndex: 10
            }}>
              <span style={{ color: '#fff', fontSize: '12px' }}>Loading...</span>
            </div>
          )}
          {purchaseOrders.length === 0 ? (
            <div style={{ fontSize: '11px', color: '#666', padding: '4px 2px' }}>No ConnectWise purchase orders.</div>
          ) : (
            <>
              <ul className="tickets">
                {purchaseOrders.map(po => (
                  <li key={po.id}>
                    <a
                      className="ticket-id"
                      //href={`https://na.myconnectwise.net/v4_6_release/services/system_io/router/openrecord.rails?recordType=PurchaseOrderFV&recid=${po.id}&companyName=${cwCompanyIdentifier || 'mys3tech'}`}
                      href={`https://na.myconnectwise.net/v4_6_release/services/system_io/router/openrecord.rails?recordType=PurchaseOrderFV&recid=${po.id}&companyName=mys3tech`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                    >#{po.number}</a>
                    <span className="ticket-title" title={po.status || ''}>{po.status || ''}</span>
                    <span className="ticket-date">{po.date || ''}</span>
                  </li>
                ))}
              </ul>
              <div className="pager" style={{ display: 'flex', gap: 8 }}>
                <button className="mini" disabled={poPage <= 1} onClick={() => {
                  const next = Math.max(1, poPage - 1);
                  if (next !== poPage) {
                    setPoPage(next);
                    onCwPurchaseOrdersPageChange && onCwPurchaseOrdersPageChange(next);
                  }
                }}>Prev</button>
                <button className="mini" disabled={!(cwPurchaseOrdersHasMore ?? (purchaseOrders.length === poPageSize))} onClick={() => {
                  const next = poPage + 1;
                  setPoPage(next);
                  onCwPurchaseOrdersPageChange && onCwPurchaseOrdersPageChange(next);
                }}>Next</button>
              </div>
            </>
          )}
        </section>
        <section>
          <h3>Insurance Documents</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
            {/* COI Document */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <strong style={{ fontSize: '12px', color: '#555' }}>Certificate of Insurance (COI)</strong>
                {!coiDocument && (
                  <button className="mini" title="Upload COI" onClick={handleAddCoiClick}><FaPlus /></button>
                )}
                <input
                  ref={coiInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={onCoiFileChange}
                />
              </div>
              {coiDocument ? (
                <ul className="attachments">
                  <li key={coiDocument.fileName} className={coiDocument.expirationDate ? 'has-expiry' : ''}>
                    <div className="attachment-main">
                      <span className="file-name" title={coiDocument.fileName}>{coiDocument.fileName}{coiDocument.size ? ` (${coiDocument.size})` : ''}</span>
                      {coiDocument.expirationDate && (
                        <span className="expiry-badge" title={`Expiration: ${formatDateMMDDYYYY(coiDocument.expirationDate)}`}>{formatDateMMDDYYYY(coiDocument.expirationDate)}</span>
                      )}
                    </div>
                    <div className="file-actions">
                      <button title="Open" onClick={() => openAttachment(coiDocument)}><FaEye /></button>
                      <button title="Download" onClick={() => downloadAttachment(coiDocument)}><FaDownload /></button>
                      <button title="Delete" onClick={removeCoiDocument}><FaTrash /></button>
                      <button
                        title={coiDocument.expirationDate ? `Change expiration (currently ${coiDocument.expirationDate})` : 'Set expiration date'}
                        onClick={() => setEditingExpiryFor(editingExpiryFor === `coi_${coiDocument.fileName}` ? null : `coi_${coiDocument.fileName}`)}
                      ><FaCalendarDays /></button>
                    </div>
                    {editingExpiryFor === `coi_${coiDocument.fileName}` && (
                      <div className="expiry-popover-wrapper">
                        <CalendarPopover
                          anchorFile={coiDocument.fileName}
                          value={coiDocument.expirationDate ? coiDocument.expirationDate.slice(0, 10) : undefined}
                          onSelect={iso => { setAttachmentExpiration(`coi_${coiDocument.fileName}`, iso); setEditingExpiryFor(null); }}
                          onClear={() => { setAttachmentExpiration(`coi_${coiDocument.fileName}`, undefined); setEditingExpiryFor(null); }}
                          onClose={() => setEditingExpiryFor(null)}
                        />
                      </div>
                    )}
                  </li>
                </ul>
              ) : (
                <div style={{ fontSize: '11px', color: '#999', padding: '4px 2px' }}>No COI uploaded yet.</div>
              )}
            </div>
            
            {/* WC Document */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <strong style={{ fontSize: '12px', color: '#555' }}>Workers' Compensation (WC)</strong>
                {!wcDocument && (
                  <button className="mini" title="Upload WC" onClick={handleAddWcClick}><FaPlus /></button>
                )}
                <input
                  ref={wcInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={onWcFileChange}
                />
              </div>
              {wcDocument ? (
                <ul className="attachments">
                  <li key={wcDocument.fileName} className={wcDocument.expirationDate ? 'has-expiry' : ''}>
                    <div className="attachment-main">
                      <span className="file-name" title={wcDocument.fileName}>{wcDocument.fileName}{wcDocument.size ? ` (${wcDocument.size})` : ''}</span>
                      {wcDocument.expirationDate && (
                        <span className="expiry-badge" title={`Expiration: ${formatDateMMDDYYYY(wcDocument.expirationDate)}`}>{formatDateMMDDYYYY(wcDocument.expirationDate)}</span>
                      )}
                    </div>
                    <div className="file-actions">
                      <button title="Open" onClick={() => openAttachment(wcDocument)}><FaEye /></button>
                      <button title="Download" onClick={() => downloadAttachment(wcDocument)}><FaDownload /></button>
                      <button title="Delete" onClick={removeWcDocument}><FaTrash /></button>
                      <button
                        title={wcDocument.expirationDate ? `Change expiration (currently ${wcDocument.expirationDate})` : 'Set expiration date'}
                        onClick={() => setEditingExpiryFor(editingExpiryFor === `wc_${wcDocument.fileName}` ? null : `wc_${wcDocument.fileName}`)}
                      ><FaCalendarDays /></button>
                    </div>
                    {editingExpiryFor === `wc_${wcDocument.fileName}` && (
                      <div className="expiry-popover-wrapper">
                        <CalendarPopover
                          anchorFile={wcDocument.fileName}
                          value={wcDocument.expirationDate ? wcDocument.expirationDate.slice(0, 10) : undefined}
                          onSelect={iso => { setAttachmentExpiration(`wc_${wcDocument.fileName}`, iso); setEditingExpiryFor(null); }}
                          onClear={() => { setAttachmentExpiration(`wc_${wcDocument.fileName}`, undefined); setEditingExpiryFor(null); }}
                          onClose={() => setEditingExpiryFor(null)}
                        />
                      </div>
                    )}
                  </li>
                </ul>
              ) : (
                <div style={{ fontSize: '11px', color: '#999', padding: '4px 2px' }}>No WC document uploaded yet.</div>
              )}
            </div>
          </div>
        </section>
        <section>
          <h3 className="row-space">
            <span>Attachments</span>
            <button className="mini" title="Add attachment" onClick={handleAddAttachmentClick}><FaPlus /></button>
            <input
              ref={attachmentInputRef}
              type="file"
              style={{ display: 'none' }}
              multiple
              onChange={onAttachmentFilesChange}
            />
          </h3>
          {attachmentItems.length === 0 && (
            <div style={{ fontSize: '11px', color: '#666', padding: '4px 2px' }}>No attachments yet.</div>
          )}
          {attachmentItems.length > 0 && (
            <ul className="attachments">
              {attachmentItems.map(a => {
                const isEditing = editingExpiryFor === a.fileName;
                return (
                  <li key={a.fileName} className={a.expirationDate ? 'has-expiry' : ''}>
                    <div className="attachment-main">
                      <span className="file-name" title={a.fileName}>{a.fileName}{a.size ? ` (${a.size})` : ''}</span>
                      {a.expirationDate && (
                        <span className="expiry-badge" title={`Expiration: ${formatDateMMDDYYYY(a.expirationDate)}`}>{formatDateMMDDYYYY(a.expirationDate)}</span>
                      )}
                    </div>
                    <div className="file-actions">
                      <button title="Open" onClick={() => openAttachment(a)}><FaEye /></button>
                      <button title="Download" onClick={() => downloadAttachment(a)}><FaDownload /></button>
                      <button title="Delete" onClick={() => removeAttachment(a.fileName)}><FaTrash /></button>
                      <button
                        title={a.expirationDate ? `Change expiration (currently ${a.expirationDate})` : 'Set expiration date'}
                        onClick={() => setEditingExpiryFor(isEditing ? null : a.fileName)}
                      ><FaCalendarDays /></button>
                    </div>
                    {isEditing && (
                      <div className="expiry-popover-wrapper">
                        <CalendarPopover
                          anchorFile={a.fileName}
                          value={a.expirationDate ? a.expirationDate.slice(0, 10) : undefined}
                          onSelect={iso => setAttachmentExpiration(a.fileName, iso)}
                          onClear={() => setAttachmentExpiration(a.fileName, undefined)}
                          onClose={() => setEditingExpiryFor(null)}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        <section>
          <h3 className="row-space">
            <span>Notes</span>
            {notesSaving && <span style={{ fontSize: '10px', color: '#888' }}>Saving...</span>}
            {!notesSaving && notes && notes.trim() && <span style={{ fontSize: '10px', color: '#5a5' }}>Saved ✓</span>}
          </h3>
          <textarea
            className="notes"
            placeholder="Add notes..."
            rows={5}
            value={notes || ''}
            onChange={e => onNotesChange?.(e.target.value)}
          />
        </section>
        {cwNotesItems.length > 0 && (
          <section style={{ position: 'relative' }}>
            <h3 className="row-space">
              <span>ConnectWise Notes</span>
              <span style={{ fontSize: 12, color: '#aaa' }}>{(cwNotesPage - 1) * notesPageSize + 1}-{(cwNotesPage - 1) * notesPageSize + cwNotesItems.length}</span>
            </h3>
            {cwLoading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
                zIndex: 10
              }}>
                <span style={{ color: '#fff', fontSize: '12px' }}>Loading...</span>
              </div>
            )}
            <ul className="cw-notes-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {cwNotesItems.map(n => (
                <li key={String(n.id ?? Math.random())} style={{ marginBottom: 10, background: '#111', borderRadius: 6, padding: 8 }}>
                  <div style={{ fontSize: 11, color: '#bbb', marginBottom: 4 }}>{n.date}{n.by ? ` • ${n.by}` : ''}</div>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: '#ddd' }}>{n.text}</div>
                </li>
              ))}
            </ul>
            <div className="pager" style={{ display: 'flex', gap: 8 }}>
              <button className="mini" disabled={cwNotesPage <= 1} onClick={() => {
                const next = Math.max(1, cwNotesPage - 1);
                if (next !== cwNotesPage) {
                  setCwNotesPage(next);
                  onCwNotesPageChange && onCwNotesPageChange(next);
                }
              }}>Prev</button>
              <button className="mini" disabled={!(cwNotesHasMore ?? (cwNotesItems.length === notesPageSize))} onClick={() => {
                const next = cwNotesPage + 1;
                setCwNotesPage(next);
                onCwNotesPageChange && onCwNotesPageChange(next);
              }}>Next</button>
            </div>
          </section>
        )}
  {/* Aggregated CW notes fallback removed intentionally to only show the paginated list */}
      </div>
    </aside>
  );
};
